/**
 * Prüfhinweise des Umsatzsteuer-Rechenkerns (Spec Sektion 4 „Prüfhinweis-Typen").
 *
 * Der Katalog legt je Typ Schwere, Standard-Aktion und Titel fest; der Text ist je Fall
 * individuell. IDs sind deterministisch `${type}_${key}` – gleiche IDs aus mehreren Regeln
 * werden zusammengeführt (lineIDs vereinigt), damit ein Hinweis je Period/Quelle nur einmal
 * erscheint.
 *
 * REVERSE_CHARGE_13B hat in Plan 1 keinen Emitter (kein Datenfeld für EU-Leistungen bis
 * Phase 4); die UI zeigt den § 13b-Hinweis statisch im ruhigen Modus.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
import {PropertyVatProfile, VatIssue, VatIssueAction, VatIssueSeverity, VatIssueType} from './types';

interface IssueCatalogEntry {
    severity: VatIssueSeverity;
    action: VatIssueAction;
    title: string;
}

export const VAT_ISSUE_CATALOG: Record<VatIssueType, IssueCatalogEntry> = {
    // BLOCKER
    PERIOD_MISSING: {severity: 'BLOCKER', action: 'OPEN_LINK', title: 'Mietverhältnis fehlt'},
    AREA_MISSING: {severity: 'BLOCKER', action: 'INLINE_AREA', title: 'Fläche der Einheit fehlt'},
    LANDLORD_CHANGE_PENDING: {severity: 'BLOCKER', action: 'OPEN_LINK', title: 'Eigentümerwechsel noch nicht ausgeführt'},
    KU_LIMIT_EXCEEDED: {severity: 'BLOCKER', action: 'OPEN_LINK', title: 'Kleinunternehmergrenze überschritten (§ 19 UStG)'},
    PROFILE_YEAR_MISMATCH: {severity: 'BLOCKER', action: 'OPEN_LINK', title: 'Leistungs- und Zuflussjahr mit unterschiedlicher Besteuerung'},
    SOURCE_DATA_INVALID: {severity: 'BLOCKER', action: 'OPEN_LINK', title: 'Datensatz unvollständig'},
    CUSTOM_RATE_INVALID: {severity: 'BLOCKER', action: 'INLINE_CATEGORY', title: 'Anderer Steuersatz ohne gültigen Satz'},
    // HINWEIS
    VAT_FLAG_MISMATCH: {severity: 'HINWEIS', action: 'INLINE_CATEGORY', title: 'MwSt-Kennzeichen widerspricht der USt-Kategorie'},
    OPTION_9_ABS_2: {severity: 'HINWEIS', action: 'CONFIRM', title: 'Option zur Steuerpflicht prüfen (§ 9 Abs. 2 UStG)'},
    AUFTEILUNG_12_11: {severity: 'HINWEIS', action: 'CONFIRM', title: 'Aufteilungsgebot bei 7 % beachten (§ 12 Abs. 2 Nr. 11 UStG)'},
    VACANCY_INTENT: {severity: 'HINWEIS', action: 'INLINE_VACANCY', title: 'Verwendungsabsicht bei Leerstand fehlt'},
    EXPENSE_DEFAULT_VAT: {severity: 'HINWEIS', action: 'ADJUST', title: 'MwSt der Ausgabe nur angenommen'},
    REVERSE_CHARGE_13B: {severity: 'HINWEIS', action: 'CONFIRM', title: 'Steuerschuldnerschaft prüfen (§ 13b UStG)'},
    LANDLORD_CHANGE_SPLIT: {severity: 'HINWEIS', action: 'CONFIRM', title: 'Eigentümerwechsel im Monat – Betrag tagesgenau geteilt'},
    TAXABLE_AT_STEUERFREI_PROFILE: {severity: 'HINWEIS', action: 'OPEN_LINK', title: 'Steuerpflichtige Vermietung bei steuerfreiem Profil'},
    KU_LIMIT_NEAR: {severity: 'HINWEIS', action: 'NONE', title: 'Kleinunternehmergrenze fast erreicht'},
    CUSTOM_ART_TAXABLE: {severity: 'HINWEIS', action: 'CONFIRM', title: 'Sonderposten als steuerbar behandelt'},
    CUSTOM_RATE_EXCLUDED: {severity: 'HINWEIS', action: 'OPEN_LINK', title: 'Anderer Steuersatz – nicht in der deutschen Voranmeldung'},
    // INFO
    CATEGORY_DERIVED: {severity: 'INFO', action: 'INLINE_CATEGORY', title: 'USt-Kategorie aus Mietverhältnis abgeleitet'},
    LEISTUNGSMONAT_AUS_DATUM: {severity: 'INFO', action: 'CONFIRM', title: 'Leistungsmonat aus Fälligkeitsdatum'},
    EXPENSE_NO_INVOICE_DATE: {severity: 'INFO', action: 'OPEN_LINK', title: 'Rechnungsdatum fehlt'},
    WARM_SPLIT_INCONSISTENT: {severity: 'INFO', action: 'OPEN_LINK', title: 'Aufteilung der Warmmiete passt nicht zum Betrag'},
    MINDEST_IST_INFO: {severity: 'INFO', action: 'NONE', title: 'Mindest-Ist-Besteuerung angewendet'},
    OFFENE_SOLLSTELLUNG_IST: {severity: 'INFO', action: 'NONE', title: 'Offene Sollstellung ohne Zufluss (Ist-Besteuerung)'},
    NEGATIVE_KZ: {severity: 'INFO', action: 'NONE', title: 'Negative Bemessungsgrundlage'},
    RUECKLASTSCHRIFT_INFO: {severity: 'INFO', action: 'NONE', title: 'Rücklastschrift ohne Auswirkung (Soll-Besteuerung)'},
    CUSTOM_RATE_ZERO_STEUERFREI: {severity: 'INFO', action: 'OPEN_LINK', title: '0 % als steuerfrei (§ 4 Nr. 12 UStG) geführt'},
};

export const issueID = (type: VatIssueType, key: string): string => `${type}_${key}`;

export interface CreateIssueOptions {
    deepLink?: string | null;
    lineIDs?: string[];
    action?: VatIssueAction;
}

export const createIssue = (type: VatIssueType, key: string, text: string, options: CreateIssueOptions = {}): VatIssue => {
    const catalog = VAT_ISSUE_CATALOG[type];
    return {
        id: issueID(type, key),
        type,
        severity: catalog.severity,
        title: catalog.title,
        text,
        action: options.action ?? catalog.action,
        deepLink: options.deepLink ?? null,
        lineIDs: [...(options.lineIDs ?? [])],
    };
};

export const isHintDismissed = (
    propertyProfile: PropertyVatProfile | undefined,
    periodID: string,
    hintID: 'OPTION_9_ABS_2' | 'AUFTEILUNG_12_11'
): boolean => !!propertyProfile?.dismissedHints?.[`${periodID}_${hintID}`];

export const SEVERITY_RANK: Record<VatIssueSeverity, number> = {BLOCKER: 0, HINWEIS: 1, INFO: 2};

const uniqueSorted = (values: string[]): string[] => Array.from(new Set(values)).sort();

export const mergeIssues = (issues: VatIssue[]): VatIssue[] => {
    const byID = new Map<string, VatIssue>();
    for (const issue of issues) {
        const existing = byID.get(issue.id);
        if (!existing) {
            byID.set(issue.id, {...issue, lineIDs: uniqueSorted(issue.lineIDs)});
            continue;
        }
        existing.lineIDs = uniqueSorted([...existing.lineIDs, ...issue.lineIDs]);
    }
    return Array.from(byID.values());
};

export const sortIssues = (issues: VatIssue[]): VatIssue[] =>
    [...issues].sort((a, b) => {
        const rank = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
        return rank !== 0 ? rank : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });

export const hasBlocker = (issues: VatIssue[]): boolean => issues.some(issue => issue.severity === 'BLOCKER');
